import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';
import { cssClass } from '~src/style';

import { IAdminProfileDetails } from '../models/IAdminProfile';
import { AdminProfileStyles } from '../styles/AdminProfileStyles';
import { AdminProfileHeader } from './AdminProfileHeader';
import { getAdminProfileSubspaces } from './AdminProfileSubspaces';
import { getAdminProfileServerUsers } from './tabs/AdminProfileUsersTabProvider';

// Style wrapper to override TabbedSpace's absolute positioning and make it fill DetailPanel
const TabbedSpaceWrapper = cssClass('admin-profile-details', 'tabbed-space-wrapper', {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  flex: 1,
  minHeight: 0, // Allow flex children to shrink
  $nest: {
    // Target the SpaceContainer div inside TabbedSpace and override absolute positioning
    '& > div[class*="SpaceContainer-space-container"]': {
      position: 'relative !important' as any,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    // Match horizontal spacing: masthead and toolbar use same inset (24px)
    '& div[class*="SpaceContainer-space-masthead-area"]': {
      paddingLeft: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
      paddingRight: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    },
    '& [data-tb-test-id="tb-spaces-toolbar"]': {
      marginLeft: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
      marginRight: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    },
    // Override SubplaceArea to use flex
    '& div[class*="SpaceContainer-space-subplace-area"]': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      position: 'relative',
      minHeight: 0,
    },
    // Override SubplaceContainer's absolute positioning and make it flex
    '& div[class*="SpaceContainer-space-subplace-container"]': {
      position: 'relative !important' as any,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: '100%',
      width: '100%',
    }
  }
});

export interface AdminProfileDetailsProps {
  profile: IAdminProfileDetails | null;
  isLoading?: boolean;
  activeSubspace: 'permissions' | 'sites' | 'users';
  onProfileSitesUpdated?: () => void;
  urlForSubspace: (subspaceName?: 'permissions' | 'sites' | 'users') => string;
}

/**
 * Admin Profile Details component
 * Displays admin profile information with tabbed content for permissions, sites, and users
 * Uses TabbedSpace pattern with style overrides to work within DetailPanel
 */
export const AdminProfileDetails: React.FC<AdminProfileDetailsProps> = ({
  profile,
  isLoading = false,
  activeSubspace,
  onProfileSitesUpdated,
  urlForSubspace,
}) => {
  if (isLoading) {
    return (
      <div className={AdminProfileStyles.EmptyState}>
        <ActivitySpinnerWidget
          testId="admin-profile-loading-spinner"
          spinnerSize={ActivitySpinnerSize.Large}
        />
      </div>
    );
  }

  if (!profile || !profile.id || !profile.permissions || !profile.sites || !profile.users) {
    return (
      <div className={AdminProfileStyles.EmptyState}>
        <div className={AdminProfileStyles.EmptyStateTitle}>
          {Messages.adminProfileDetailsNoSelectionTitle()}
        </div>
        <div className={AdminProfileStyles.EmptyStateDescription}>
          {Messages.adminProfileDetailsNoSelectionDescription()}
        </div>
      </div>
    );
  }
// Update the profile subspaces when the profile updated
  const adminProfileSystemUsers = getAdminProfileServerUsers(profile.users ?? []);
  const subspaces = getAdminProfileSubspaces(profile, onProfileSitesUpdated, adminProfileSystemUsers);

  return (
    <div className={TabbedSpaceWrapper}>
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={activeSubspace}
        urlForSubspace={urlForSubspace}
        mastheadElement={
          <AdminProfileHeader
            profile={profile}
          />
        }
        testIdPrefix="admin-profile"
        spaceItemName={profile.name}
      />
    </div>
  );
};

AdminProfileDetails.displayName = 'AdminProfileDetails';
